import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { forkJoin, mergeMap, Observable, of, switchMap, tap } from 'rxjs';
import { ReactiveComponentBase } from 'src/app/core/components/ReactiveComponentBase';
import { AuthService } from 'src/app/core/services/auth.service';
import {
  MediaType,
  Photo,
  PhotoResultSet,
  PortalApiService,
  PropertyDetails,
  PropertySummaryResultSet,
} from 'src/app/services/portal-api.service';
import { PropertyListingService } from 'src/app/services/property.service';
import { IndicationSidePanelInputModel } from 'src/app/shared/components/indication-side-panel/IndicationSidePanelInputModel';
import { SimilarPropertiesComponent } from 'src/app/shared/components/similar-properties/similar-properties.component';
import { PropertyInsightsComponentComponent } from '../../shared/components/property-insights-component/property-insights-component.component';
import { AsideContentComponent } from './aside-content/aside-content.component';
import { CostsComponent } from './costs/costs.component';
import { DetailSectionComponent } from './detail-section/detail-section.component';
import { FeaturesSectionComponent } from './features-section/features-section.component';
import { FinancingSectionComponent } from './financing-section/financing-section.component';
import { FloorPlanSectionComponent } from './floor-plan-section/floor-plan-section.component';
import { HeroSectionComponent } from './hero-section/hero-section.component';
import { MapAreaSectionComponent } from './map-area-section/map-area-section.component';
import { PropertyDescriptionComponent } from './property-description/property-description.component';
import { PropertyServicesComponent } from './property-services/property-services.component';
import { TopNavigationComponent } from './top-navigation/top-navigation.component';
import { AuctionSectionComponent } from './auction-section/auction-section.component';
import { ExpertsCallbackComponent } from "../../shared/components/experts-callback/experts-callback.component";
import { SeoMetaTagService } from 'src/app/services/seo-meta-tags.service';
import { ValidationProblemDetails } from 'src/app/services/identity-api.service';

@Component({
  selector: 'app-property-details',
  standalone: true,
  templateUrl: './property-details.component.html',
  styleUrl: './property-details.component.css',
  imports: [
    CommonModule,
    HeroSectionComponent,
    DetailSectionComponent,
    FeaturesSectionComponent,
    FloorPlanSectionComponent,
    TranslocoDirective,
    MapAreaSectionComponent,
    FinancingSectionComponent,
    SimilarPropertiesComponent,
    PropertyInsightsComponentComponent,
    AsideContentComponent,
    PropertyDescriptionComponent,
    PropertyServicesComponent,
    CostsComponent,
    TopNavigationComponent,
    AuctionSectionComponent,
    ExpertsCallbackComponent
  ],
})
export class PropertyDetailsComponent extends ReactiveComponentBase<[PropertyDetails, void, PropertySummaryResultSet, PhotoResultSet]> {
  public propertyId!: string;
  protected propertyCode!: string;
  protected propertyLat: number = 0;
  protected propertyLon: number = 0;
  protected isLoggedIn: boolean = false;
  protected isLoggedIn$: Observable<boolean> = this.authService.isLoggedIn();
  protected sidePanelData: Array<IndicationSidePanelInputModel> = [];
  public similarProperties: PropertySummaryResultSet | undefined;
  public photos: PhotoResultSet | undefined;
  public floorPlans: Photo[] | undefined;
  public isOpen: boolean = false;

  constructor(
    private modelService: PropertyListingService,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private portalApi: PortalApiService,
    private translocoService: TranslocoService,
    private seoService: SeoMetaTagService
  ) {
    super();
  }

  protected populateSidePanelData(shouldIncludeFloorPlan: boolean) {
    this.translocoService
      .selectTranslateObject('property-details.top-navigation')
      .pipe(
        tap((t: any) => {
          if (shouldIncludeFloorPlan) {
            this.sidePanelData = [
              {
                label: t['description'],
                scrollTo: 'paragraph',
              },
              {
                label: t['details'],
                scrollTo: 'detail',
              },
              {
                label: t['features'],
                scrollTo: 'features',
              },
              {
                label: t['map-area'],
                scrollTo: 'mapArea',
              },
              {
                label: t['floor-plan'],
                scrollTo: 'floorPlan',
              },
              {
                label: t['property-insights'],
                scrollTo: 'insights',
              },
              {
                label: t['services'],
                scrollTo: 'services',
              },
              {
                label: t['fees'],
                scrollTo: 'fees',
              },
              {
                label: t['financing'],
                scrollTo: 'financing',
              },
            ];
          } else {
            this.sidePanelData = [
              {
                label: t['description'],
                scrollTo: 'paragraph',
              },
              {
                label: t['details'],
                scrollTo: 'detail',
              },
              {
                label: t['features'],
                scrollTo: 'features',
              },
              {
                label: t['map-area'],
                scrollTo: 'mapArea',
              },
              {
                label: t['property-insights'],
                scrollTo: 'insights',
              },
              {
                label: t['services'],
                scrollTo: 'services',
              },
              {
                label: t['fees'],
                scrollTo: 'fees',
              },
              {
                label: t['financing'],
                scrollTo: 'financing',
              },
            ];
          }
        }),
      )
      .subscribe();
  }

  override getModel(): Observable<[PropertyDetails, void, PropertySummaryResultSet, PhotoResultSet]> {
    return this.activatedRoute.params.pipe(
      tap((t: Params) => {
        this.propertyCode = t['propertyId'];
      }),
      // First get the model to set propertyId
      switchMap(() => this.modelService.getPropertyById(this.propertyCode)),
      tap(model => {
        this.propertyId = model.id as string;
      }),
      // Then execute all parallel operations
      switchMap(model => {
        const recentlyViewed$ = this.isLoggedIn$.pipe(
          mergeMap(isLoggedIn => 
            isLoggedIn ? this.portalApi.addRecentlyViewed(model.id ?? '') : of(undefined)
        ));
        
        const relatedProperties$ = this.portalApi.getRelatedProperties(model.id ?? '', 1, 7);
        const photos$ = this.portalApi.getPhotos(model.id ?? '');
        
        return forkJoin([
          of(model),
          recentlyViewed$,
          relatedProperties$,
          photos$
        ]);
      })
    )
  }

  protected override onGetModel([theModel, _, relatedProperties, photos]: [PropertyDetails, void, PropertySummaryResultSet, PhotoResultSet]): void {
    this.propertyCode = theModel.code!;
    this.seoService.SetMetaCanonicalLinkByUrl(`/properties/${this.propertyCode}`);
    this.seoService.SetMetaTitle(`${theModel.title!} | Τιμή: ${theModel?.price}€`);
    this.seoService.SetOgMeta("title", `${theModel.title!} | Τιμή: ${theModel?.price}€`);
    this.seoService.SetOgMeta("description", theModel.description!);
    this.seoService.SetOgMeta("type", "website");
    this.seoService.SetOgMeta("url", window.location.href);
    this.seoService.SetOgMeta("image", theModel.defaultPhoto?.uri ?? '');
    this.seoService.SetTwitterCard();

    // Handle recently viewed (no action needed as it's just a tracking call)
    
    // Handle related properties
    this.similarProperties = relatedProperties;
    
    // Handle photos
    this.photos = photos;

    this.floorPlans = this.photos?.items?.filter(
      (x) => x.type === MediaType.PropertyElevation,
    );
    const shouldIncludeFloorPlan = (this.floorPlans && this.floorPlans?.length > 0) ?? false;
    this.populateSidePanelData(shouldIncludeFloorPlan);
  }

  protected override onError(error: ValidationProblemDetails): ValidationProblemDetails {
    this.populateSidePanelData(false);
    return error
  }

  public toggleAside() {
    this.isOpen = !this.isOpen;
  }

  // private sortPhotos(resultSet?: PhotoResultSet): PhotoResultSet | undefined {
  //   if (!resultSet || !Array.isArray(resultSet.items)) return resultSet;

  //   resultSet.items.sort((a, b) => {
  //     // First: isDefault === true first
  //     if (a.isDefault !== b.isDefault) {
  //       return (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
  //     }

  //     // Second: photos with URI come before those without
  //     const aHasUri = !!a.uri;
  //     const bHasUri = !!b.uri;
  //     return (bHasUri ? 1 : 0) - (aHasUri ? 1 : 0);
  //   });

  //   return resultSet;
  // }
}
